<template>
  <sidebar-layout-item
    class="cmp-contact-form-additional"
    :class="{ ['in-edit']: inEdit }"
  >
    <q-form ref="form" novalidate @submit.prevent>
      <template v-if="usecase === CF_USECASE.SERVER">
        <h5>
          {{ $t('scf.additional.title') }}
          <div v-if="!inEdit" class="float-right">
            <ws-button
              :type="WsButtonEnum.Type.GHOST"
              :size="mobile ? WsButtonEnum.Size.SMALL : WsButtonEnum.Size.LARGE"
              icon="edit"
              @click="$emit('edit')"
            />
            <ws-tooltip>{{ $t('general.edit') }}</ws-tooltip>
          </div>
        </h5>

        <small>{{ $t('scf.additional.info') }}</small>

        <h6 class="q-mt-4xl q-mb-md">
          {{ $t('scf.name.title') }}
        </h6>
        <ws-input
          v-model="model.name"
          :disable="!inEdit"
          :placeholder="$t('scf.name.placeholder')"
          :label="$t('scf.name.label')"
          :restrict="serverNameValidation.restriction"
          debounce="300"
          lazy-rules
          :rules="[required, ...serverNameValidation.rules, uniqueServerName]"
          @input="reEmit"
        />
        <!-- <p v-else class="text-ws-base150">
          {{ model.name }}
        </p> -->

        <hr class="ws-default q-my-xl" />

        <h6 :class="desktop ? 'q-mb-lg' : 'q-mb-md'">
          {{ $t('product.properties.monitoring.title') }}
        </h6>

        <property-radio
          v-model="model.monitoring"
          path="product.properties.monitoring"
          :in-edit="inEdit"
          :options="productOptions.monitoring"
          :period="model.period"
          :no-spacing-x="false"
          :no-spacing-y="false"
          @input="reEmit"
        />

        <hr class="ws-default q-my-xl" />

        <h6 :class="desktop ? 'q-mb-lg' : 'q-mb-md'">
          {{ $t('product.properties.ip_address.title') }}
        </h6>

        <property-radio
          v-model="model.ipAddress"
          path="product.properties.ip_address"
          :in-edit="inEdit"
          :options="productOptions.ipAddress"
          :period="model.period"
          :no-spacing-x="false"
          :no-spacing-y="false"
          @input="reEmit"
        />

        <hr class="ws-default q-my-xl" />

        <property-os
          v-model="model.os"
          :in-edit="inEdit"
          :options="productOptions.os"
          :licences.sync="model.licences"
          @input="reEmit"
          @update:licences="reEmit"
        />

        <template v-if="productOptions.backup">
          <hr class="ws-default q-my-xl" />

          <h6 :class="desktop ? 'q-mb-lg' : 'q-mb-md'">
            {{ $t('scf.additional_services.title') }}
          </h6>

          <property-slider
            v-model="model.backup"
            label="product.properties.backup.title"
            :in-edit="inEdit"
            @input="reEmit"
          />
        </template>
      </template>

      <!-- Currently just CF_USECASE.MONITORING -->
      <template v-else>
        <h5>
          {{ $t(`scf.${usecase}.additional.title`) }}
          <div v-if="!inEdit">
            <ws-button
              :type="WsButtonEnum.Type.GHOST"
              :size="mobile ? WsButtonEnum.Size.SMALL : WsButtonEnum.Size.LARGE"
              icon="edit"
              @click="$emit('edit')"
            />
            <ws-tooltip>{{ $t('general.edit') }}</ws-tooltip>
          </div>
        </h5>

        <small v-if="inEdit || hasSelected.any" class="inline-block q-mb-4xl">
          {{ $t(`scf.${usecase}.additional.info`) }}
        </small>
        <ws-message
          v-else
          class="non-selected q-mt-md"
          banner
          type="gray"
          icon="info"
          :messages="`scf.${usecase}.additional.non_selected_info`"
        />

        <div v-for="(section, index) in productOptions" :key="section.name">
          <template v-if="inEdit || hasSelected[section.name]">
            <hr v-if="index" class="ws-default q-my-xl" />

            <h6
              v-if="section.name !== 'general'"
              :class="desktop ? 'q-mb-lg' : 'q-mb-md'"
            >
              {{ $t(getKey(section.name, 'title')) }}
            </h6>

            <ws-checkbox-list
              v-model="model"
              uid-prop="name"
              mode="item"
              :show-selected-only="!inEdit"
              :in-edit="inEdit"
              :items="section.options"
              :columns="section.name === 'general' ? 1 : 2"
              @input="reEmit"
            >
              <template v-slot="scope">
                <small class="text-ws-base150">{{
                  $t(getPropsKey(section.name, scope.item.name, 'label'))
                }}</small>
              </template>
              <!--
              TODO: $te method does not work
              <div
                v-if="$te(getPropsKey(section.name, scope.item.name, 'info'))"
              > -->
              <template v-slot:info="scope">
                <small
                  v-if="
                    !$t(getPropsKey(section.name, scope.item.name, 'info'), {
                      _silent: true,
                    }).startsWith('product.properties.')
                  "
                  class="q-mt-sm"
                >
                  {{ $t(getPropsKey(section.name, scope.item.name, 'info')) }}
                </small>
              </template>
            </ws-checkbox-list>
          </template>
        </div>
      </template>
      <div
        v-if="inEdit"
        class="full-width text-center buttons-wrap relative-position"
      >
        <div v-if="backUrl">
          <ws-button
            v-if="!mobile"
            :type="WsButtonEnum.Type.SECONDARY"
            :href="backUrl"
            :label="$t('uikit:general.back')"
            class="absolute-left"
            icon="arrow_left"
          ></ws-button>
        </div>
        <div>
          <ws-button submit :type="WsButtonEnum.Type.PRIMARY" :disable="loading" @click="submit">
            {{ $t('scf.additional_services.save_btn') }}
          </ws-button>
        </div>
        <div v-if="backUrl">
          <ws-button
            v-if="mobile"
            :type="WsButtonEnum.Type.SECONDARY"
            :href="backUrl"
            :label="$t('uikit:general.back')"
            class="q-mt-lg"
            icon="arrow_left"
          ></ws-button>
        </div>
      </div>
    </q-form>
  </sidebar-layout-item>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { QForm } from 'quasar';

import { cloneDeep } from 'lodash-es';
import { getSnakedKeyFactory } from '@odin/utils';
import {
  validateForm,
  required,
  addValidations,
  productPropertyList,
  propertyValidation,
  uniqueServerName,
} from '@/services/validation/validation.service.ts';
import type { PropertyValidation } from '@/services/validation/validation.service.d';
import WsInput from '@WS_Components/WsInput.vue';
import WsCheckboxList from '@/components/WsCheckboxList.vue';
import { WsButton, WsButtonEnum, WsTooltip } from '@odin/components-vue2';
import WsMessage from '../WsMessageOld.vue';
import config from '@/services/config.service';
import { CF_USECASE } from '@/components/contactForm/const.enum';
import SidebarLayoutItem from '@/components/SidebarLayoutItem.vue';
import PricePeriod from '@/components/PricePeriod.vue';
import PropertySlider from '@/components/xSell/PropertySlider.vue';
import PropertyRadio from '@/components/xSell/PropertyRadio.vue';
import PropertyOs from '@/components/xSell//PropertyOs.vue';

@Component({
  components: {
    QForm,
    WsInput,
    WsCheckboxList,
    WsTooltip,
    WsButton,
    WsMessage,
    SidebarLayoutItem,
    PricePeriod,
    PropertyOs,
    PropertySlider,
    PropertyRadio,
  },
})
export default class ContactFormAdditional extends Vue {
  @Prop({ default: () => ({}) })
  readonly value!: any;
  @Prop()
  readonly usecase!: string;
  @Prop({ default: () => ({}) })
  readonly productOptions!: any;
  @Prop(Boolean)
  readonly inEdit!: boolean;

  loading = false;
  backUrl = config.dedicatedServersUrl || '';
  serverNameValidation!: PropertyValidation;
  WsButtonEnum = WsButtonEnum;

  data() {
    return {
      required,
      uniqueServerName,
      CF_USECASE,
    };
  }

  get model() {
    return cloneDeep(this.value);
  }
  set model(value: any) {
    this.$emit('input', value);
  }

  get hasSelected(): Record<string, boolean> {
    const result: Record<string, boolean> = { any: false };
    for (const key in this.productOptions) {
      const section = this.productOptions[key];
      const sectionResult = !!section.options?.filter(
        (opt: any) => this.model[opt.name]
      ).length;
      result[section.name] = sectionResult;
      if (sectionResult) {
        result.any = true;
      }
    }
    return result;
  }

  created() {
    addValidations(productPropertyList);
    this.serverNameValidation = propertyValidation('server_name');
  }

  submit() {
    validateForm(this.$refs.form as Element & QForm).then((result: boolean) => {
      if (!result) {
        return;
      }
      // else
      this.$emit('continue');
    });
  }

  getKey(prop: string, key: string): string {
    this.getKey = getSnakedKeyFactory('scf.' + this.usecase);
    return this.getKey(prop, key);
  }

  getPropsKey(sectionName: string, itemName: string, key: string): string {
    this.getPropsKey = getSnakedKeyFactory(
      'product.properties.' + this.usecase
    );
    return this.getPropsKey(sectionName, itemName, key);
  }

  reEmit() {
    this.$emit('input', this.model);
  }
}
</script>

<style lang="scss" scoped>
.cmp-contact-form-additional {
  .in-edit {
    .theme--cary & {
      @include ws-top-stripe;
    }
    position: relative;
  }

  .buttons-wrap {
    margin-top: map-get($space-4xl, y);

    @media (max-width: $ws-breakpoint-tablet-max) {
      margin-top: map-get($space-xl, y);
    }
  }
}
</style>
